import type {
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Directive, ElementRef, Input, inject } from '@angular/core';
import type { FormControl } from '@angular/forms';
import { TimeUtils } from '@freelancer/time-utils';
import { isDefined } from '@freelancer/utils';
import type { Subscription } from 'rxjs';
import { BehaviorSubject, filter, switchMap } from 'rxjs';

@Directive({
  selector: '[flAutoGrow]',
  exportAs: 'autogrow',
  standalone: true,
})
export class AutoGrowDirective implements OnInit, OnChanges, OnDestroy {
  element = inject<ElementRef<HTMLElement>>(ElementRef);
  private time = inject(TimeUtils);

  @Input()
  set flAutoGrow(formControl: FormControl<string> | undefined) {
    this.formControlSubject$.next(formControl);
  }
  /**
   * Maximum height (in pixels) the element will automatically grow to.
   * Can still be expanded manually beyond the max height.
   *
   * The minimum height is always the default height based on a textarea's `rows`.
   */
  @Input() flAutoGrowMaxHeight = 72;

  private formControlSubject$ = new BehaviorSubject<
    FormControl<string> | undefined
  >(undefined);
  private valueChangeSubscription?: Subscription;

  ngOnChanges(changes: SimpleChanges): void {
    if ('flAutoGrow' in changes) {
      this.formControlSubject$.next(changes.flAutoGrow.currentValue);
    }

    if ('flAutoGrowMaxHeight' in changes) {
      const { currentValue, previousValue } = changes.flAutoGrowMaxHeight;

      // Only adjust if max height changes
      if (isDefined(previousValue) && currentValue < previousValue) {
        this.time.setTimeout(() => this.adjust('shrink'));
      } else if (isDefined(previousValue) && currentValue > previousValue) {
        this.time.setTimeout(() => this.adjust('grow'));
      }
    }
  }

  ngOnInit(): void {
    this.valueChangeSubscription = this.formControlSubject$
      .pipe(
        filter(isDefined),
        switchMap(formControl => formControl.valueChanges),
      )
      .subscribe(() => this.adjust());

    const formControl = this.formControlSubject$.value;
    if (formControl?.value) {
      // Wait for the textarea to render the scroll bar before adjusting
      this.time.setTimeout(() => this.adjust());
    }
  }

  ngOnDestroy(): void {
    this.valueChangeSubscription?.unsubscribe();
  }

  public adjust(adjust?: 'shrink' | 'grow'): void {
    let textArea: HTMLElement;
    if (this.element.nativeElement instanceof HTMLTextAreaElement) {
      textArea = this.element.nativeElement;
    } else if (this.element.nativeElement.tagName === 'FL-TEXTAREA') {
      textArea = this.element.nativeElement.children.item(0) as HTMLElement;
    } else {
      throw new Error('Not supported element!');
    }

    let borderOffset = textArea.offsetHeight - textArea.clientHeight;

    // Check if we need to force adjust due to max height change
    if (
      adjust === 'shrink' &&
      textArea.clientHeight > this.flAutoGrowMaxHeight
    ) {
      textArea.style.height = `${Math.min(
        textArea.scrollHeight + borderOffset,
        this.flAutoGrowMaxHeight,
      )}px`;
    }
    if (adjust === 'grow' && textArea.clientHeight < this.flAutoGrowMaxHeight) {
      textArea.style.height = `${Math.min(
        textArea.scrollHeight + borderOffset,
        this.flAutoGrowMaxHeight,
      )}px`;
    }

    if (
      // if element actually exists and is rendered
      textArea.scrollHeight > 0 &&
      // and there's room to grow, then try to autogrow
      (textArea.scrollHeight < this.flAutoGrowMaxHeight ||
        textArea.offsetHeight < this.flAutoGrowMaxHeight)
    ) {
      // Snap to text to determine the proper height
      textArea.style.overflow = 'hidden';
      textArea.style.height = 'auto';
      textArea.style.overflowY = 'auto';

      // Borders aren't counted in the scrollHeight or clientHeight,
      // but are in the `style.height`, so we need to add the extra height manually
      borderOffset = textArea.offsetHeight - textArea.clientHeight;
      textArea.style.height = `${Math.min(
        textArea.scrollHeight + borderOffset,
        this.flAutoGrowMaxHeight,
      )}px`;

      // Undo styling/snap changes
      textArea.style.overflow = 'auto';
    }
  }
}
